'use client'

import cornerFullWidth from '@/assets/corner-full-width.svg'
import patternBg from '@/assets/pattern-bg.jpg'
import type { ImageData, VideoData } from '@/frontend-types'
import { tagCMSBlock } from '@/helpers/layout'
import { cx } from 'cva'
import { useMotionValueEvent, useScroll } from 'motion/react'
import Image from 'next/image'
import { useRef, useState, type FC, type PropsWithChildren } from 'react'
import { Corridor } from '../atoms/Corridor'
import { Grid } from '../atoms/Grid'
import { Video } from '../atoms/Video'
import { FullEdgeDeco } from '../molecules/FullEdgeDeco'

export type FeatureImageTextBlockProps = {
	mediaType: 'image' | 'video'
	image?: ImageData
	video?: VideoData
	hasCaption: boolean
}

export const FeatureImageTextBlock: FC<PropsWithChildren<FeatureImageTextBlockProps>> = ({
	mediaType,
	image,
	video,
	hasCaption,
	children,
}) => {
	const ref = useRef<HTMLDivElement>(null)

	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ['start end', 'end start'],
	})
	const [progress, setProgress] = useState(0)

	useMotionValueEvent(scrollYProgress, 'change', (latest) => {
		setProgress(latest)
	})

	return (
		<section
			className={cx(
				tagCMSBlock(['standalone', 'stackable', 'fullwidth-image']),
				'relative flex flex-col justify-end overflow-hidden',
			)}
			ref={ref}
		>
			<style jsx>{`
				.styled-bg {
					background-image: ${image ? `url("${image.src}")` : 'none'};
				}
				.scroll-translate-y {
					transform: translateY(${30 * progress}%);
				}
				.styled-caption-bg {
					background-image: ${`url("${patternBg.src}")`};
				}
			`}</style>
			{!hasCaption && (
				<div className="absolute top-0 right-0 z-10 h-12 tablet-l:h-16">
					<Image src={cornerFullWidth} alt="" width={200} height={64} className="h-full w-auto" />
				</div>
			)}
			{/* Parallax media */}
			<div className="absolute inset-0 overflow-hidden">
				<div className="relative w-full h-full translate-y-[-20%] flex items-center justify-center">
					{mediaType === 'image' && image && (
						<div className="styled-bg scroll-translate-y bg-cover bg-center w-[120%] h-[120%] will-change-transform" />
					)}
					{mediaType === 'video' && video && (
						<div className="scroll-translate-y w-[120%] h-[120%] will-change-transform">
							<Video video={video} controls={false} autoPlay muted loop playsInline />
						</div>
					)}
				</div>
			</div>
			{/* Image spacer */}
			<div className="h-[150vw] mobile-l:h-[100vw] tablet-p:h-[75vw] tablet-l:h-[66vw] desktop:h-[50vw] max-h-[816px]" />
			<FullEdgeDeco />
			{hasCaption && (
				<div className="styled-caption-bg relative z-10 bg-blue bg-cover bg-center bg-no-repeat py-6xl text-white">
					{/* Add padding bottom to the corridor to compensate for the edge substruct */}
					<Corridor className="pb-object-edge">
						<Grid>
							<div className="col-span-full tablet-l:col-span-10">{children}</div>
						</Grid>
					</Corridor>
				</div>
			)}
		</section>
	)
}

export default FeatureImageTextBlock
